/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'ISP | Шаблоны проектирования и приёмы рефакторинга',
  description: 'Шаблоны проектирования и приёмы рефакторинга, которые помогают следовать принципу разделения интерфейса'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      section: "section",
      h1: "h1",
      p: "p",
      h2: "h2",
      a: "a",
      pre: "pre",
      code: "code",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_components.section, {
      children: [_jsx(_components.h1, {
        children: "Шаблоны проектирования и приёмы рефакторинга"
      }), _jsx(_components.p, {
        children: "Следовать принципу разделения интерфейса помогают такие шаблоны проектирования как Адаптер, а также приёмы выделения интерфейса и множественного наследования."
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Адаптер"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://refactoring.guru/design-patterns/adapter",
            children: "Адаптер"
          }), " — шаблон проектирования, который позволяет объектам с несовместимыми интерфейсами работать вместе."]
        }), _jsx(_components.p, {
          children: "С точки зрения ISP этот шаблон помогает держать интерфейсы чистыми и понятными, а при необходимости совместить несовместимые модули через специальную прослойку (адаптер)."
        }), _jsxs(_components.p, {
          children: ["Приложение ", _jsx(_components.a, {
            href: "https://bespoyasov.ru/blog/kursovik/",
            children: "Курсовик"
          }), " показывает курс доллара к рублю. Оно берёт данные курсов с сайта Центрального банка России. Центробанк отдаёт их в формате XML, а Курсовик работает с JSON."]
        }), _jsx(_components.p, {
          children: "Адаптер помогает «подружить» модули, которые работают с XML, и модули, которые работают с JSON."
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "const ApiClient = {\n  async getXml(url: string): Promise<XmlString> {\n    const response = await fetch(url)\n    const data = await response.text()\n    return data\n  }\n}\n\nconst xmlJsonAdapter = (xml: XmlString): JsonString => {\n  // ...конвертируем xml в json\n  return json\n}\n\nconst parseCourse = (data: JsonString): CourseDict => {\n  // ...\n  return course\n}\n\n(async () => {\n  const data = await ApiClient.getXml('api_endpoint')\n  const course = parseCourse(xmlJsonAdapter(data))\n})()\n"
          })
        }), _jsxs(_components.p, {
          children: ["Также адаптер ", _jsx(_components.a, {
            href: "https://bespoyasov.ru/blog/adapter-pattern/",
            children: "помогает справляться"
          }), " со «сломанным» API от бекенда и преобразованием одних структур данных в другие."]
        }), _jsx(_components.p, {
          children: "Из минусов можно назвать:"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: "добавление ещё одной абстракции в кодовую базу проекта;"
          }), "\n", _jsx(_components.li, {
            children: "при создании нового адаптера нужно найти все места, где требуется его использовать."
          }), "\n"]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Выделение интерфейса"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://refactoring.guru/ru/extract-interface",
            children: "Выделение интерфейса"
          }), " — это приём, при котором одинаковые методы и поля выносят в отдельный интерфейс."]
        }), _jsxs(_components.p, {
          children: ["В качестве примера можно вернуться к ", _jsx(_components.a, {
            href: "/isp/in-real-life",
            children: "Койну из прошлого раздела"
          }), ". Интерфейс ", _jsx(_components.code, {
            children: "Record"
          }), " — это выделенный общий интерфейс, который включает в себя общие для траты и дохода поля."]
        }), _jsxs(_components.p, {
          children: ["Выделение интерфейса тесно связано не только с ISP, но и с LSP. Например, оно используется при поиске ", _jsx(_components.a, {
            href: "https://www.dotnetcurry.com/patterns-practices/1259/liskov-substitution-principle-perspective",
            children: "корня композиции"
          }), " и как вспомогательный инструмент для ", _jsx(_components.a, {
            href: "/lsp/patterns",
            children: "выделения суперкласса"
          }), "."]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Множественное наследование"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9C%D0%BD%D0%BE%D0%B6%D0%B5%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%BD%D0%BE%D0%B5_%D0%BD%D0%B0%D1%81%D0%BB%D0%B5%D0%B4%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5",
            children: "Множественное наследование"
          }), " используется, например, чтобы реализовать функциональность нескольких интерфейсов:"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class Horse implements Animal, Transport {/*...*/}\n"
          })
        }), _jsxs(_components.p, {
          children: ["В TypeScript такое наследование реализуется через ", _jsx(_components.a, {
            href: "https://www.typescriptlang.org/docs/handbook/mixins.html",
            children: "миксины"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["До этих пор в книге для простоты повествования мы пропускали специальную функцию ", _jsx(_components.code, {
            children: "applyMixins"
          }), ", которая копирует функциональность из родительских классов:"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "function applyMixins(derivedCtor: any, baseCtors: any[]) {\n  baseCtors.forEach(baseCtor => {\n    Object.getOwnPropertyNames(baseCtor.prototype).forEach(name => {\n      Object.defineProperty(derivedCtor.prototype, name, Object.getOwnPropertyDescriptor(baseCtor.prototype, name))\n    })\n  })\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["Чтобы пример с классом ", _jsx(_components.code, {
            children: "Horse"
          }), " выше сработал, нам необходимо использовать ", _jsx(_components.code, {
            children: "applyMixins"
          }), " следующим образом:"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "applyMixins(Horse, [Animal, Transport])\n"
          })
        }), _jsx(_components.p, {
          children: "Тогда множественное наследование будет работать, как мы ожидаем."
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Материалы к разделу"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://refactoring.guru/design-patterns/adapter",
              children: "Адаптер"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://bespoyasov.ru/blog/adapter-pattern/",
              children: "Лекарство от сломанной обратной совместимости"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://github.com/torokmark/design_patterns_in_typescript/blob/master/adapter/adapter.ts",
              children: "Реализация адаптера на TypeScript"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://refactoring.guru/ru/extract-interface",
              children: "Выделение интерфейса"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://www.dotnetcurry.com/patterns-practices/1259/liskov-substitution-principle-perspective",
              children: "Liskov Substitution Principle and the Composition Root"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://ru.wikipedia.org/wiki/%D0%9C%D0%BD%D0%BE%D0%B6%D0%B5%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%BD%D0%BE%D0%B5_%D0%BD%D0%B0%D1%81%D0%BB%D0%B5%D0%B4%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5",
              children: "Множественное наследование"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://www.typescriptlang.org/docs/handbook/mixins.html",
              children: "Mixins in TypeScript"
            })
          }), "\n"]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Вопросы"
        }), _jsx(Quiz, {
          name: "isp-patterns-1"
        }), _jsx(Quiz, {
          name: "isp-patterns-2"
        })]
      })]
    });
  }
}
export default MDXContent;
